import { useState, useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { TicketTypeRow } from '../TicketTypeRow'
import {
  checkForChanges,
  convertToListItem,
  createBatchPayload,
} from '../lib/dataConverters'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { refreshTicketTypes } from '../../../../store/project'
import { intl } from '../../../../i18n'
import { useProjectPrivateContext } from '../../../context/projectContext'
import WbsItemTypeRepository from '../../../../infrastructure/repository/WbsItemTypeRepository'
import { GridApi } from 'ag-grid-community'

interface UseTicketTypeDataProps {
  functionUuid: string
  gridApi?: GridApi
  ticketTypesFallback?: { items?: TicketTypeRow[] }
}

interface UseTicketTypeDataReturn {
  rowData: TicketTypeRow[]
  originalData: TicketTypeRow[]
  hasUnsavedChanges: boolean
  loading: boolean
  error: string | null
  handleReload: () => Promise<void>
  handleSubmit: () => Promise<void>
  handleDataChange: (newData: TicketTypeRow[]) => void
  clearError: () => void
}

/**
 * チケットタイプデータの管理とAPI連携を行うフック
 */
export const useTicketTypeData = ({
  functionUuid,
  gridApi,
  ticketTypesFallback,
}: UseTicketTypeDataProps): UseTicketTypeDataReturn => {
  const dispatch = useDispatch()
  const { projectUuid } = useProjectPrivateContext()
  const [rowData, setRowData] = useState<TicketTypeRow[]>([])
  const [originalData, setOriginalData] = useState<TicketTypeRow[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const repository = useMemo(() => new WbsItemTypeRepository(), [])

  /**
   * エラークリア
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * データ変更時のハンドラー
   */
  const handleDataChange = useCallback(
    (newData: TicketTypeRow[]) => {
      setRowData(newData)
      const hasChanges = checkForChanges(newData, originalData)
      setHasUnsavedChanges(hasChanges)
      // データ変更時はエラーをクリア
      setError(null)
    },
    [originalData]
  )

  /**
   * displayOrderがnullのものに採番して振り直す
   * 既存の値がある場合はそれを保持し、nullの場合は連番で振り直す
   */
  function assignDisplayOrderIfNull(rows: TicketTypeRow[]): TicketTypeRow[] {
    // displayOrderでソート（nullは後ろに配置）
    const sorted = [...rows].sort((a, b) => {
      if (a.displayOrder == null && b.displayOrder == null) return 0
      if (a.displayOrder == null) return 1
      if (b.displayOrder == null) return -1
      return a.displayOrder - b.displayOrder
    })

    // 全体に0から連番を振り直す
    return sorted.map((row, index) => ({
      ...row,
      displayOrder: index,
    }))
  }

  /**
   * チケットタイプデータを読み込み
   */
  const handleReload = useCallback(async () => {
    setLoading(true)
    try {
      const editableTypes = await repository.getEditableTickets()
      const convertedData = editableTypes.map((item: any, index: number) =>
        convertToListItem(item, index)
      )
      // 採番前のデータ（displayOrder=nullを含む）をoriginalDataに保存
      setOriginalData(convertedData.map(item => ({ ...item })))
      // displayOrderがnullのものに採番してrowDataに保存
      const assignedData = assignDisplayOrderIfNull(convertedData)
      setRowData(assignedData)
      setHasUnsavedChanges(false)
    } catch (error) {
      // エラー時はフォールバックデータを使用
      if (ticketTypesFallback?.items) {
        const fallbackData = [...ticketTypesFallback.items]
        setRowData(fallbackData)
        setOriginalData(fallbackData.map(item => ({ ...item })))
        setHasUnsavedChanges(false)
      }
    } finally {
      setLoading(false)
    }
  }, [ticketTypesFallback, repository])

  /**
   * データを保存
   */
  const handleSubmit = useCallback(async () => {
    if (!hasUnsavedChanges) return

    // 編集中のセルがある場合はフォーカスを外して入力を確定
    gridApi?.stopEditing()

    // バリデーション: プリセット以外で名称またはアイコンが空の行をチェック
    const invalidRows = rowData.filter(
      row =>
        !row.isPreset &&
        (!row.name ||
          row.name.trim() === '' ||
          !row.iconUrl ||
          row.iconUrl.trim() === '')
    )

    if (invalidRows.length > 0) {
      // BulkSheet形式でバリデーションエラーメッセージを作成
      const errorMessages = invalidRows.map(row => {
        const rowNumber = rowData.indexOf(row) + 1
        const errors: string[] = []
        if (!row.name || row.name.trim() === '') {
          errors.push(
            `${intl.formatMessage({ id: 'ticketType.name' })} > ${intl.formatMessage({ id: 'ticketType.required' })}`
          )
        }
        if (!row.iconUrl || row.iconUrl.trim() === '') {
          errors.push(
            `${intl.formatMessage({ id: 'ticketType.icon' })} > ${intl.formatMessage({ id: 'ticketType.required' })}`
          )
        }
        return `#${rowNumber} > ${errors.join(', ')}`
      })

      const text =
        errorMessages.length === 1
          ? errorMessages[0]
          : errorMessages.map(message => `- ${message}`).join('\n')

      // 黄色の警告でスナックバーに表示
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: 'global.warning.businessError' }),
          text,
        })
      )
      return
    }

    setLoading(true)
    try {
      // 変更内容からバッチAPIのペイロードを作成
      const batchPayload = createBatchPayload(rowData, originalData)

      // バッチAPIを呼び出し
      await repository.batchUpdateEditableTickets(batchPayload)

      // 成功時にoriginalDataを更新して変更状態をリセット
      setOriginalData(rowData.map(item => ({ ...item })))
      setHasUnsavedChanges(false)

      // 保存完了メッセージを表示
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'registration.complete' }),
        })
      )

      // データを再読み込みして最新状態に同期
      await handleReload()

      // Redux Storeのチケットタイプを更新
      dispatch(refreshTicketTypes())
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'チケットタイプの保存中にエラーが発生しました'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [
    rowData,
    originalData,
    hasUnsavedChanges,
    functionUuid,
    handleReload,
    gridApi,
    repository,
    dispatch,
  ])

  // プロジェクトが変更された時に再読み込み（初回マウント時も含む）
  useEffect(() => {
    handleReload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUuid])

  return {
    rowData,
    originalData,
    hasUnsavedChanges,
    loading,
    error,
    handleReload,
    handleSubmit,
    handleDataChange,
    clearError,
  }
}
